import React from 'react';
import { createPortal } from 'react-dom';
import { dashboardInputClass } from './dashboard/DashboardChrome';

function formatPrice(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const fieldClass = `${dashboardInputClass} py-2 text-sm`;

/**
 * Full-screen material search: product grid with dark-mode support.
 */
export default function MaterialSearchShoppingModal({
  isOpen,
  onClose,
  locationCountry,
  setLocationCountry,
  locationRegion,
  setLocationRegion,
  locationCity,
  setLocationCity,
  locationRegions,
  testLocation,
  locationValidation,
  locationValidationMessage,
  materialSearch,
  setMaterialSearch,
  searchMaterials,
  materialSearchLoading,
  materialResults,
  saveMaterialToHistory,
  onSelectMaterial,
}) {
  if (!isOpen || typeof document === 'undefined') return null;

  const locationReady = !!locationCountry && !!locationRegion && locationCity.trim().length > 0;
  const handleSearchSubmit = () => {
    if (locationValidation === 'valid' && materialSearch.trim().length >= 2) {
      searchMaterials(materialSearch.trim());
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100130] flex items-center justify-center bg-slate-900/55 p-3 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Material shopping search"
    >
      <div className="flex h-[min(94vh,900px)] w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-slate-50 shadow-2xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900 dark:ring-white/5">
        {/* Top bar */}
        <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900 sm:px-6 sm:py-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-slate-100 sm:text-xl">Shopping for materials</h2>
              <p className="text-sm text-gray-500 dark:text-slate-400">Compare prices and add items to your quote</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Location row */}
          <div className="mb-3">
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-slate-400">Near</label>
            <div className="grid gap-2 sm:grid-cols-3">
              <div>
                <input
                  type="text"
                  value={locationCity}
                  onChange={(e) => setLocationCity(e.target.value)}
                  placeholder="Enter city (e.g. Toronto or Dallas)"
                  className={fieldClass}
                />
              </div>
              <div>
                <select value={locationRegion} onChange={(e) => setLocationRegion(e.target.value)} className={fieldClass}>
                  <option value="">Select state/province</option>
                  {locationRegions.map((region) => (
                    <option key={region.code} value={region.code}>
                      {region.name} ({region.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select value={locationCountry} onChange={(e) => setLocationCountry(e.target.value)} className={fieldClass}>
                  <option value="CA">Canada (CA)</option>
                  <option value="US">United States (US)</option>
                </select>
              </div>
            </div>
            <div className="mt-2 flex">
              <button
                type="button"
                onClick={testLocation}
                disabled={locationValidation === 'testing' || !locationReady}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-slate-600 disabled:opacity-60"
              >
                {locationValidation === 'testing' ? '…' : 'Verify location'}
              </button>
            </div>
            {locationValidation && (
              <p
                className={`mt-1 text-xs ${
                  locationValidation === 'valid'
                    ? 'text-green-600 dark:text-emerald-400'
                    : locationValidation === 'invalid'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-blue-600 dark:text-cyan-400'
                }`}
              >
                {locationValidationMessage}
              </p>
            )}
          </div>

          {/* Search pill */}
          <div className="mx-auto max-w-3xl">
            <div className="flex items-stretch overflow-hidden rounded-full border border-gray-300 bg-white shadow-sm transition focus-within:border-gray-400 focus-within:shadow-md dark:border-slate-600 dark:bg-slate-800 dark:focus-within:border-slate-500">
              <div className="flex flex-1 items-center pl-4">
                <svg className="h-5 w-5 shrink-0 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearchSubmit();
                  }}
                  disabled={locationValidation !== 'valid' || !locationReady}
                  placeholder={
                    locationValidation === 'valid'
                      ? 'Search products (e.g. 2x4 lumber, drywall screws)…'
                      : 'Set a valid location first'
                  }
                  className="min-w-0 flex-1 border-0 bg-transparent px-3 py-3 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 dark:text-slate-100 dark:placeholder-slate-500 dark:disabled:bg-slate-800/50 dark:disabled:text-slate-500"
                />
              </div>
              <button
                type="button"
                onClick={handleSearchSubmit}
                disabled={materialSearchLoading || !materialSearch.trim() || locationValidation !== 'valid' || !locationReady}
                className="shrink-0 bg-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-slate-600 disabled:opacity-60"
              >
                {materialSearchLoading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>
        </div>

        {/* Results — product grid */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/50 px-4 py-5 dark:bg-slate-950/50 sm:px-6">
          {materialSearchLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="mb-4 h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-primary dark:border-slate-700 dark:border-t-cyan-400" />
              <p className="text-sm text-gray-600 dark:text-slate-400">Finding products…</p>
            </div>
          )}

          {!materialSearchLoading && materialResults.length === 0 && (
            <div className="mx-auto max-w-md py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-600">
                <svg className="h-8 w-8 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <p className="text-base font-medium text-gray-800 dark:text-slate-200">Search for building materials</p>
              <p className="mt-2 text-sm text-gray-500 dark:text-slate-400">
                Results appear here in a shopping-style grid. Try a product name and press Search.
              </p>
            </div>
          )}

          {!materialSearchLoading && materialResults.length > 0 && (
            <>
              <p className="mb-4 text-sm text-gray-600 dark:text-slate-400">
                <span className="font-medium text-gray-900 dark:text-slate-100">{materialResults.length}</span> result
                {materialResults.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {materialResults.map((mat, index) => {
                  const priceRaw = mat.price ?? mat.extracted_price ?? 0;
                  const priceNum = typeof priceRaw === 'number' ? priceRaw : parseFloat(priceRaw) || 0;
                  const isHistory = !!mat.isHistory;
                  return (
                    <article
                      key={mat.id != null ? String(mat.id) : `${mat.name}-${index}`}
                      className={`flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition hover:shadow-md dark:bg-slate-800/80 ${
                        isHistory
                          ? 'border-cyan-200 ring-1 ring-cyan-100 dark:border-cyan-800 dark:ring-cyan-900/50'
                          : 'border-gray-200 dark:border-slate-700'
                      }`}
                    >
                      <div className="flex h-40 items-center justify-center bg-white p-3 dark:bg-slate-800">
                        {mat.thumbnail ? (
                          <img
                            src={mat.thumbnail}
                            alt=""
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-50 text-gray-300 dark:bg-slate-900 dark:text-slate-600">
                            <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex min-h-0 flex-1 flex-col border-t border-gray-100 p-3 dark:border-slate-700">
                        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm leading-snug text-[#1a0dab] dark:text-cyan-300">
                          {mat.link && !isHistory ? (
                            <a href={mat.link} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {mat.name}
                            </a>
                          ) : (
                            <span className={isHistory ? 'text-cyan-900 dark:text-cyan-200' : 'text-gray-900 dark:text-slate-100'}>
                              {mat.name}
                            </span>
                          )}
                        </h3>
                        {mat.source && (
                          <p className="mt-1 truncate text-xs text-gray-600 dark:text-slate-400">
                            {isHistory ? (
                              <span className="font-medium text-cyan-700 dark:text-cyan-400">Your saved materials</span>
                            ) : (
                              <span>{mat.source}</span>
                            )}
                          </p>
                        )}
                        <div className="mt-auto pt-3">
                          <div className="flex items-baseline gap-1">
                            <span className={`text-xl font-medium ${isHistory ? 'text-cyan-900 dark:text-cyan-200' : 'text-gray-900 dark:text-slate-100'}`}>
                              ${formatPrice(priceNum)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-slate-500">/ unit</span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                onSelectMaterial({
                                  name: mat.name,
                                  price: priceNum,
                                })
                              }
                              className="flex-1 rounded-lg bg-primary px-3 py-2 text-center text-sm font-semibold text-white hover:bg-primary-dark"
                            >
                              Use for quote
                            </button>
                            {!isHistory && (
                              <button
                                type="button"
                                onClick={() => saveMaterialToHistory({ name: mat.name, price: priceNum })}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-700"
                              >
                                Save
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
